import {Avatar, Checkbox, Text} from '@chakra-ui/react';
import {useNavigate} from 'react-router-dom';
import {getChannelThumbnail} from '../../../utils/image';
import {DataTable, Page} from '../../../components';
import {routes} from '../../../config/routes';
import {api} from '../../../api';
import {ChannelType} from '../../../config';
import React, {useCallback, useState} from 'react';

const ALL_MARKET_TYPES = ['stock', 'crypto', 'viop', 'commodity'];
const FUND_SORT_FIELDS = new Set([
  'dailyReturn',
  'weeklyReturn',
  'monthlyReturn',
  'yearlyReturn',
  'totalValue',
  'name',
]);

const buildFundParams = rawParams => {
  const marketParams = {...rawParams};

  if (marketParams.query === '') {
    delete marketParams.query;
  }

  if (marketParams.sortBy && typeof marketParams.sortBy === 'string') {
    const [field, order] = marketParams.sortBy.split(':');

    if (FUND_SORT_FIELDS.has(field)) {
      marketParams.sortBy = field;
      marketParams.sortOrder = order || 'desc';
    } else {
      marketParams.sortBy = 'dailyReturn';
      marketParams.sortOrder = 'desc';
    }
  }

  return marketParams;
};

const buildVirtualChannel = ({item, isFund}) => ({
  ...item,
  id: null,
  _id: null,
  name: item.name,
  marketCode: !isFund ? item.code : undefined,
  fundCode: isFund ? item.code : undefined,
  type: isFund ? 'fund' : 'market',
  thumbnail: item.logo || null,
  messageCount: 0,
  memberCount: 0,
  isVirtual: true,
});

const getChannelKey = channel =>
  String(channel?.fundCode || channel?.marketCode || '').trim().toUpperCase();

const sortCombinedResults = (items, sortBy) => {
  if (!sortBy || typeof sortBy !== 'string') {
    return items;
  }

  const [field, order = 'desc'] = sortBy.split(':');
  const direction = order === 'asc' ? 1 : -1;

  const sorted = [...items].sort((a, b) => {
    const aValue = a?.[field];
    const bValue = b?.[field];

    if (field === 'name') {
      return String(aValue || '').localeCompare(String(bValue || ''), 'tr') * direction;
    }

    if (typeof aValue === 'number' || typeof bValue === 'number') {
      return ((Number(aValue) || 0) - (Number(bValue) || 0)) * direction;
    }

    const aDate = aValue ? new Date(aValue).getTime() : 0;
    const bDate = bValue ? new Date(bValue).getTime() : 0;
    return (aDate - bDate) * direction;
  });

  return sorted;
};

// Kanal id'leri için canlı üye sayılarını satırlara ekler. Sayım alınamazsa
// liste yine açılır, sütunda "-" görünür.
const attachLiveMemberCounts = async (data, mode) => {
  const channelIds = (data?.results || []).map(channel => channel.id).filter(Boolean);
  if (channelIds.length === 0) return data;

  let counts = {};
  try {
    counts = (await api.getChannelMemberCounts(channelIds, mode)).data || {};
  } catch (error) {
    counts = {};
  }

  return {
    ...data,
    results: data.results.map(channel => ({
      ...channel,
      liveMemberCounts: counts[channel.id] || null,
    })),
  };
};

const AllChannels = ({
  category,
  isRestricted,
  onlyAdminCanPost,
  type,
  types,
  navigationTarget = 'edit',
}) => {
  const navigate = useNavigate();

  // Normal (Manuel), Kısıtlı ve piyasa/fon kategori sayfalarında "Üye Sayısı",
  // artırılıp azaltılan memberCount sayacı yerine gerçek sayımı gösterir:
  // - Kısıtlı: izinli kullanıcı sayısı (İzinli Kullanıcılar PDF'i ile aynı liste)
  // - Normal ve piyasa/fon: kanala katılmış üye sayısı
  const isMarketOrFundCategory = ['borsa', 'kripto', 'viop', 'emtia', 'fon'].includes(
    category,
  );
  const liveCountMode =
    type === 'normal' && !category && !types
      ? isRestricted
        ? 'allowed'
        : 'members'
      : isMarketOrFundCategory
        ? 'members'
        : null;
  const [excludeAdmins, setExcludeAdmins] = useState(true);

  const fetchData = useCallback(
    async options => {
      const params = {...options};

      // Map category to market type
      let marketType = null;
      if (category === 'borsa') marketType = 'stock';
      else if (category === 'kripto') marketType = 'crypto';
      else if (category === 'viop') marketType = 'viop';
      else if (category === 'emtia') marketType = 'commodity';

      const isFund = category === 'fon';

      if (marketType || isFund) {
        // 1. Fetch Markets/Funds (Pagination Source)
        let marketsRes;
        const marketParams = {...params, type: marketType};
        // Clean up params that might not be needed for markets API if strict
        delete marketParams.category;

        if (isFund) {
          // Fix for Fund API Validation parameters
          if (marketParams.query === '') {
            delete marketParams.query;
          }

          // Handle sortBy (DataTable sends "field:desc", Fund API expects sortBy and sortOrder separately)
          if (marketParams.sortBy && typeof marketParams.sortBy === 'string') {
            const [field, order] = marketParams.sortBy.split(':');
            const allowedSortFields = [
              'dailyReturn',
              'weeklyReturn',
              'monthlyReturn',
              'yearlyReturn',
              'totalValue',
              'name',
            ];

            if (allowedSortFields.includes(field)) {
              marketParams.sortBy = field;
              marketParams.sortOrder = order || 'desc';
            } else {
              // Fallback for fields not in allowed list (like createdAt)
              marketParams.sortBy = 'dailyReturn';
              marketParams.sortOrder = 'desc';
            }
          }

          marketsRes = await api.getFunds(marketParams);
        } else {
          marketsRes = await api.getMarkets(marketParams);
        }

        // 2. Bu piyasa/fonlar için açılmış gerçek kanalları çek.
        // Kanal `category` alanına göre DEĞİL, türe (market/fund) göre çekilir:
        // piyasa/fon kanalları oluşturulurken category doldurulmuyor (varsayılan
        // 'other'), bu yüzden category=borsa vb. filtresi hiçbir kanal bulamıyor ve
        // her satır 0 üyeli sanal kanal olarak görünüyordu. Eşleştirme koda göre.
        const channelsRes = await api.getAllChannels({
          type: isFund ? 'fund' : 'market',
          includeInactive: true,
          limit: 5000,
        });
        const channelByCode = new Map(
          (channelsRes.data.results || []).map(channel => [
            getChannelKey(channel),
            channel,
          ]),
        );

        // 3. Merge
        const mergedResults = (marketsRes.data.results || []).map(item => {
          const code = item.code;
          const existingChannel = channelByCode.get(
            String(code || '').trim().toUpperCase(),
          );

          if (existingChannel) return existingChannel;

          // Create virtual channel object
          return {
            // Preserve other market data if needed
            ...item,
            // Ensure ID is null so we know it's virtual
            id: null,
            _id: null,

            name: item.name,
            marketCode: !isFund ? code : undefined,
            fundCode: isFund ? code : undefined,
            type: isFund ? 'fund' : 'market',
            thumbnail: item.logo || null,
            messageCount: 0,
            memberCount: 0,
            isVirtual: true,
          };
        });

        const mergedData = {
          results: mergedResults,
          totalResults:
            marketsRes.data.totalResults || marketsRes.data.total || 0,
        };
        return liveCountMode
          ? attachLiveMemberCounts(mergedData, liveCountMode)
          : mergedData;
      }

      const requestedTypes = String(types || '')
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
      const isAggregatedAllView =
        !category &&
        !type &&
        requestedTypes.length > 0 &&
        requestedTypes.every(item => ['market', 'fund', 'normal'].includes(item));

      if (isAggregatedAllView) {
        const currentPage = Number(params.page || 1);
        const currentLimit = Number(params.limit || 10);
        const actualChannelsParams = {
          query: params.query,
          types,
          isRestricted,
          includeInactive: params.includeInactive,
          limit: 5000,
          page: 1,
        };

        const actualChannelsRes = await api.getAllChannels(actualChannelsParams);
        const actualChannels = (actualChannelsRes?.data?.results || []).filter(
          c => !!(c?.name || c?.label),
        );
        const marketOrFundChannelMap = new Map(
          actualChannels
            .filter(channel => channel.type === 'market' || channel.type === 'fund')
            .map(channel => [getChannelKey(channel), channel]),
        );

        const combinedResults = [];

        if (requestedTypes.includes('market')) {
          const marketResponses = await Promise.all(
            ALL_MARKET_TYPES.map(marketApiType =>
              api.getMarkets({
                type: marketApiType,
                limit: 1000,
                query: params.query,
              }),
            ),
          );

          marketResponses.forEach(response => {
            (response?.data?.results || []).forEach(item => {
              const existingChannel = marketOrFundChannelMap.get(
                String(item?.code || '').trim().toUpperCase(),
              );

              if (existingChannel) {
                combinedResults.push(existingChannel);
              } else if (isRestricted !== true) {
                combinedResults.push(
                  buildVirtualChannel({
                    item,
                    isFund: false,
                  }),
                );
              }
            });
          });
        }

        if (requestedTypes.includes('fund')) {
          const fundsResponse = await api.getFunds(
            buildFundParams({
              limit: 2000,
              query: params.query,
              sortBy: params.sortBy,
            }),
          );

          (fundsResponse?.data?.results || []).forEach(item => {
            const existingChannel = marketOrFundChannelMap.get(
              String(item?.code || '').trim().toUpperCase(),
            );

            if (existingChannel) {
              combinedResults.push(existingChannel);
            } else if (isRestricted !== true) {
              combinedResults.push(
                buildVirtualChannel({
                  item,
                  isFund: true,
                }),
              );
            }
          });
        }

        if (requestedTypes.includes('normal')) {
          combinedResults.push(
            ...actualChannels.filter(channel => channel.type === 'normal'),
          );
        }

        const sortedResults = sortCombinedResults(combinedResults, params.sortBy);
        const paginatedResults = sortedResults.slice(
          (currentPage - 1) * currentLimit,
          currentPage * currentLimit,
        );

        return {
          results: paginatedResults,
          page: currentPage,
          limit: currentLimit,
          totalPages: Math.ceil(sortedResults.length / currentLimit) || 1,
          totalResults: sortedResults.length,
        };
      }

      // Default behavior for other categories or 'all'
      if (category) {
        params.category = category;
      }
      if (typeof isRestricted !== 'undefined') {
        params.isRestricted = isRestricted;
      }
      if (typeof onlyAdminCanPost !== 'undefined') {
        params.onlyAdminCanPost = onlyAdminCanPost;
      }
      if (type) {
        params.type = type;
      }
      if (types) {
        params.types = types;
      }
      // Yönetim panelinde pasif kanallar da listelenmeli. Backend, bu parametre
      // gönderilmezse `isActive: { $ne: false }` filtresi uyguluyor; yani pasife
      // düşmüş bir kanal panelde hiç görünmüyor ve yeniden aktifleştirilemiyordu.
      params.includeInactive = true;

      // Varsayılan sıralama: en yeni kanal en üstte.
      // Backend'in paginate eklentisi sortBy verilmediğinde 'createdAt' (ARTAN)
      // kullanıyor; bu da yeni eklenen kanalı son sayfaya atıyor ve panelde
      // "kanal eklendi ama listede yok" izlenimi yaratıyordu.
      if (!params.sortBy) {
        params.sortBy = 'createdAt:desc';
      }
      const response = await api.getAllChannels(params);
      if (response.data && response.data.results) {
        response.data.results = response.data.results.filter(
          c => !!(c.name || c.label),
        );
      }
      return liveCountMode
        ? attachLiveMemberCounts(response.data, liveCountMode)
        : response.data;
    },
    [category, isRestricted, onlyAdminCanPost, type, types, liveCountMode],
  );

  const onRow = async item => {
    if (item.id) {
      if (navigationTarget === 'chat') {
        navigate(routes.channelChat.getPath(item.id));
        return;
      }

      navigate(routes.editChannel.getPath(item.id));
      return;
    }

    if (navigationTarget !== 'chat' || !item?.isVirtual) {
      return;
    }

    let response;
    if (item.type === 'market' && item.marketCode) {
      response = await api.initiateMarketChannel(item.marketCode);
    } else if (item.type === 'fund' && item.fundCode) {
      response = await api.initiateFundChannel(item.fundCode);
    }

    if (response?.data?.id) {
      navigate(routes.channelChat.getPath(response.data.id));
    }
  };

  return (
    <Page
      action={
        liveCountMode ? (
          <Checkbox
            isChecked={excludeAdmins}
            onChange={e => setExcludeAdmins(e.target.checked)}
            whiteSpace="nowrap">
            Üye sayısında adminleri hariç tut
          </Checkbox>
        ) : undefined
      }>
      <DataTable
        key={`${category}-${isRestricted}-${onlyAdminCanPost}-${type}-${types}`}
        queryEnabled
        deleteVisible={false}
        onRow={onRow}
        columns={[
          {
            header: 'Logo',
            accessorKey: 'thumbnail',
            cell: ({getValue, row}) => (
              <Avatar
                name={row?.original?.name}
                src={getChannelThumbnail(row.original)}
                size={'sm'}
              />
            ),
          },
          {
            header: 'İsim',
            accessorKey: 'name',
          },

          liveCountMode
            ? {
                header:
                  liveCountMode === 'allowed' ? 'İzinli Kullanıcı' : 'Üye Sayısı',
                // id sunucu tarafı sıralama alanıdır (sayaç); gösterilen değer canlı sayım.
                id: 'memberCount',
                accessorFn: row => row.liveMemberCounts,
                cell: ({getValue, row}) => {
                  // Henüz açılmamış (sanal) piyasa/fon kanalının üyesi yoktur.
                  if (row?.original?.isVirtual) return 0;
                  const counts = getValue();
                  if (!counts) return '-';
                  return excludeAdmins ? counts.excludingAdmins : counts.total;
                },
              }
            : {
                header: 'Üye Sayısı',
                accessorKey: 'memberCount',
              },
          {
            header: 'Vip',
            accessorKey: 'type',
            cell: ({getValue}) => {
              return (
                <Text>{getValue() === ChannelType.Vip ? 'Evet' : 'Hayır'}</Text>
              );
            },
          },
          {
            // Pasif kanallar da listelendiği için ayırt edilebilmeli.
            header: 'Aktiflik',
            accessorKey: 'isActive',
            cell: ({getValue, row}) => {
              // Piyasa/fon için üretilen sanal kanallar henüz kayıtlı değildir;
              // onlar için aktiflik bilgisi anlamlı olmaz.
              if (row?.original?.isVirtual) {
                return <Text color="gray.500">—</Text>;
              }
              const inactive = getValue() === false;
              return (
                <Text color={inactive ? 'red.500' : 'green.500'}>
                  {inactive ? 'Pasif' : 'Aktif'}
                </Text>
              );
            },
          },
        ]}
        fetchData={fetchData}
        filters={{category, isRestricted, onlyAdminCanPost, type, types}}
      />
    </Page>
  );
};

export default AllChannels;
